import { Fragment, useEffect, useRef, useState } from "react";

import { Dialog, Transition } from "@headlessui/react";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { LockClosedIcon } from "@heroicons/react/24/solid";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { trackPromise } from "react-promise-tracker";
import { useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router";

import { getStrapiClient } from "@/lib/strapiClient";

import Logo from "../assets/images/logo-acna.svg";
import { setSessionStorage } from "../lib/http/cookies";
import { setUser } from "../redux/userSlice";

export default function LogIn() {
	const { t, i18n } = useTranslation();
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();

	const [openAlert, setOpenAlert] = useState(false);

	const cancelButtonRef = useRef(null);

	useEffect(() => {
		const lang = searchParams.get("lang");
		if (lang) {
			console.info("LANG: ", lang);
			i18n.changeLanguage(lang);
		}
	}, [i18n, searchParams]);

	const formik = useFormik({
		initialValues: {
			identifier: "",
			password: "",
		},
		onSubmit: (values) => {
			const submitData = async () => {
				try {
					const client = getStrapiClient();
					const response = await client.fetch("auth/local", {
						method: "POST",
						headers: { "Content-Type": "application/json" },
						body: JSON.stringify(values),
					});
					if (!response.ok) throw new Error("Auth failed");
					const data = await response.json();
					setSessionStorage("token", data.jwt);
					dispatch(setUser(data.user));
					navigate("/");
				} catch (error) {
					console.error(error);
					setOpenAlert(true);
				}
			};

			trackPromise(submitData());
		},
	});

	return (
		<>
			<div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 py-12 sm:px-6 lg:px-8">
				<div className="w-full max-w-md space-y-8">
					<div>
						<img className="mx-auto h-20 w-auto" src={Logo} alt="ACNA" />
						<h2 className="mt-6 text-center font-extrabold text-3xl text-gray-900">{t("login.welcome")}</h2>
					</div>
					<form className="mt-8 space-y-6" onSubmit={formik.handleSubmit}>
						<input type="hidden" name="remember" defaultValue="true" />
						<div className="-space-y-px rounded-md shadow-xs">
							<div>
								<label htmlFor="identifier" className="sr-only">
									{t("login.username")}
								</label>
								<input
									id="identifier"
									name="identifier"
									type="text"
									required
									className="relative block w-full appearance-none rounded-none rounded-t-md border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-500 focus:z-10 focus:border-blue-500 focus:outline-hidden focus:ring-blue-500 sm:text-sm"
									placeholder="Usuari"
									onChange={formik.handleChange}
									value={formik.values.identifier}
								/>
							</div>
							<div>
								<label htmlFor="password" className="sr-only">
									{t("login.password")}
								</label>
								<input
									id="password"
									name="password"
									type="password"
									autoComplete="current-password"
									required
									className="relative block w-full appearance-none rounded-none rounded-b-md border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-500 focus:z-10 focus:border-blue-500 focus:outline-hidden focus:ring-blue-500 sm:text-sm"
									placeholder="Contrasenya"
									onChange={formik.handleChange}
									value={formik.values.password}
								/>
							</div>
						</div>

						<div>
							<button
								type="submit"
								className="group relative flex w-full justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 font-medium text-sm text-white hover:bg-blue-700 focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
							>
								<span className="absolute inset-y-0 left-0 flex items-center pl-3">
									<LockClosedIcon className="h-5 w-5 text-blue-500 group-hover:text-blue-400" aria-hidden="true" />
								</span>
								{t("login.login")}
							</button>
						</div>
					</form>
				</div>
			</div>
			{/* Modal alert */}
			<Transition.Root show={openAlert} as={Fragment}>
				<Dialog
					as="div"
					className="relative z-50"
					initialFocus={cancelButtonRef}
					onClose={setOpenAlert}
				>
					<Transition.Child
						as={Fragment}
						enter="ease-out duration-300"
						enterFrom="opacity-0"
						enterTo="opacity-100"
						leave="ease-in duration-200"
						leaveFrom="opacity-100"
						leaveTo="opacity-0"
					>
						<div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
					</Transition.Child>

					<div className="fixed inset-0 z-50 overflow-y-auto">
						<div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
							<Transition.Child
								as={Fragment}
								enter="ease-out duration-300"
								enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
								enterTo="opacity-100 translate-y-0 sm:scale-100"
								leave="ease-in duration-200"
								leaveFrom="opacity-100 translate-y-0 sm:scale-100"
								leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
							>
								<Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
									<div className="sm:flex sm:items-start">
										<div className="mx-auto flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
											<ExclamationTriangleIcon className="h-6 w-6 text-red-600" aria-hidden="true" />
										</div>
										<div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
											<Dialog.Title as="h3" className="font-medium text-gray-900 text-lg leading-6">
												{t("login.error.title")}
											</Dialog.Title>
											<div className="mt-2">
												<p className="text-gray-500 text-sm">{t("login.error.description")}</p>
											</div>
										</div>
									</div>
									<div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
										<button
											type="button"
											className="inline-flex w-full justify-center rounded-md bg-blue-600 px-4 py-2 font-semibold text-sm text-white shadow-sm hover:bg-blue-500 focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 sm:ml-3 sm:w-auto"
											onClick={() => setOpenAlert(false)}
											ref={cancelButtonRef}
										>
											{t("login.error.button")}
										</button>
									</div>
								</Dialog.Panel>
							</Transition.Child>
						</div>
					</div>
				</Dialog>
			</Transition.Root>
		</>
	);
}
